import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import AdminNav from "@/components/AdminNav";
import BulkBar, { RowCheckbox, SelectAllBox, useSelection } from "@/components/BulkBar";
import useTableState from "@/hooks/useTableState";
import { TableToolbar, Th, filterSelectCls, matchSearch, sortRows } from "@/components/DataTable";

const btn = "rounded border px-2 py-1 font-mono2 text-[9px] font-bold uppercase whitespace-nowrap";

export default function AdminUsers() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [nl, setNl] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const uts = useTableState("u", { sort: "created_at", dir: "desc" });
  const ats = useTableState("a", { sort: "at", dir: "desc" });
  const nts = useTableState("n", { sort: "email", dir: "asc" });
  const usel = useSelection();
  const [bulkBusy, setBulkBusy] = useState(false);
  const [dialog, setDialog] = useState(null);   // { kind: "cancel" | "delete" | "access", user, sub? }
  const [confirmEmail, setConfirmEmail] = useState("");
  const [access, setAccess] = useState({ level: "free", until: "", note: "" });

  const load = useCallback(async () => {
    try {
      const [u, a, ns, nl_] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/audit"),
        api.get("/admin/newsletter/stats"),
        api.get("/admin/newsletter/subscribers"),
      ]);
      setUsers(u.data.users);
      setAudit(a.data.logs);
      setNl(ns.data);
      setSubs(nl_.data.subscribers);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (email, action, extra = {}) => {
    try {
      await api.post("/admin/users/action", { email, action, ...extra });
      toast.success(t("admin.action_ok"));
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openCancel = async (u) => {
    setDialog({ kind: "cancel", user: u, loading: true });
    try {
      const r = await api.get("/admin/users/subscription", { params: { email: u.email } });
      setDialog({ kind: "cancel", user: u, sub: r.data });
    } catch (e) {
      toast.error(formatApiError(e));
      setDialog(null);
    }
  };

  const openAccess = (u) => {
    setAccess({ level: u.access_level || "free", until: (u.contributor_until || "").slice(0, 10), note: "" });
    setDialog({ kind: "access", user: u });
  };

  const saveAccess = async () => {
    try {
      await api.put("/admin/users/access-level", {
        email: dialog.user.email, level: access.level, note: access.note.trim(),
        until: access.level === "contributor" && access.until ? access.until : null,
      });
      toast.success(t("admin.access_saved"));
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bulkUsers = async (action) => {
    if (bulkBusy) return;  // guard against multi-click double submission
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/users/bulk", { emails: [...usel.sel], action });
      toast.success(`${t("admin.action_ok")}: ${r.data.done}${r.data.skipped_admins ? ` · ${r.data.skipped_admins} admin ✗` : ""}`);
      usel.clear();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const exportCsv = async () => {
    const r = await api.get("/admin/newsletter/export", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "newsletter.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const uRows = (() => {
    let r = users.filter((u) => matchSearch(u, uts.get("q"), ["email", "name"]));
    const plan = uts.get("plan");
    if (plan) r = r.filter((u) => (u.plan || "free") === plan);
    const acc = uts.get("access");
    if (acc) r = r.filter((u) => (u.access_level || "free") === acc);
    const ver = uts.get("verified");
    if (ver) r = r.filter((u) => String(!!u.email_verified) === ver);
    const sus = uts.get("suspended");
    if (sus) r = r.filter((u) => String(!!u.suspended) === sus);
    const promo = uts.get("promo");
    if (promo) r = r.filter((u) => (u.promo_code || "") === promo);
    return sortRows(r, uts.get("sort"), uts.get("dir"));
  })();

  const selectable = uRows.filter((u) => u.role !== "admin").map((u) => u.email);

  const auditActions = useMemo(() => [...new Set(audit.map((a) => a.action).filter(Boolean))].sort(), [audit]);
  const aRows = (() => {
    let r = audit.filter((a) => matchSearch(a, ats.get("q"), ["actor", "action", "target", "detail"]));
    const act = ats.get("action");
    if (act) r = r.filter((a) => a.action === act);
    const from = ats.get("from");
    if (from) r = r.filter((a) => (a.at || "") >= from);
    const to = ats.get("to");
    if (to) r = r.filter((a) => (a.at || "") <= `${to}T23:59:59`);
    return sortRows(r, ats.get("sort"), ats.get("dir"));
  })();

  const nStatuses = useMemo(() => [...new Set(subs.map((s) => s.status).filter(Boolean))].sort(), [subs]);
  const nRows = (() => {
    let r = subs.filter((s) => matchSearch(s, nts.get("q"), ["email"]));
    const st = nts.get("status");
    if (st) r = r.filter((s) => s.status === st);
    return sortRows(r, nts.get("sort"), nts.get("dir"));
  })();

  if (loading) return <p className="pt-16 text-center font-mono2 text-sm text-slate-500">{t("common.loading")}</p>;

  const badge = (ok, txt) => (
    <span className={`rounded-full border px-2 py-0.5 font-mono2 text-[9px] font-bold uppercase ${ok ? "border-emerald-500/50 text-emerald-400" : "border-slate-600 text-slate-500"}`}>{txt}</span>
  );

  return (
    <div data-testid="admin-users-page">
      {dialog?.kind === "cancel" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="cancel-sub-dialog">
          <div className="w-full max-w-md rounded-xl border border-orange-500/40 bg-[#0B1120] p-5">
            <h3 className="mb-2 font-display text-lg font-black uppercase text-orange-400">{t("admin.cancel_sub_title")}</h3>
            <p className="mb-1 font-mono2 text-[11px] text-slate-300">{dialog.user.email}</p>
            <p className="mb-3 text-sm text-slate-400">{t("admin.cancel_sub_body")}</p>
            {dialog.loading ? (
              <p className="mb-4 font-mono2 text-xs text-slate-500">{t("common.loading")}</p>
            ) : dialog.sub?.has_subscription ? (
              <p className="mb-4 font-mono2 text-xs text-slate-200" data-testid="cancel-sub-access-until">
                {t("admin.cancel_sub_until")} <b className="text-[#FFD600]">{dialog.sub.access_until ? new Date(dialog.sub.access_until).toLocaleDateString() : "—"}</b>
                {dialog.sub.cancel_at_period_end && <span className="ml-2 text-slate-500">· {t("admin.cancel_sub_already")}</span>}
              </p>
            ) : (
              <p className="mb-4 font-mono2 text-xs text-amber-400">{t("admin.cancel_sub_none")}</p>
            )}
            <div className="flex justify-end gap-2">
              <button data-testid="cancel-sub-dismiss" onClick={() => setDialog(null)} className={`${btn} border-slate-600 text-slate-300 hover:bg-slate-700/30`}>{t("admin.cancel")}</button>
              <button
                data-testid="cancel-sub-confirm"
                disabled={dialog.loading || !dialog.sub?.has_subscription || dialog.sub?.cancel_at_period_end}
                onClick={() => act(dialog.user.email, "cancel_subscription")}
                className={`${btn} border-orange-500 bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 disabled:opacity-40`}
              >
                {t("admin.cancel_sub_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {dialog?.kind === "delete" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="delete-account-dialog">
          <div className="w-full max-w-md rounded-xl border border-red-500/60 bg-[#0B1120] p-5">
            <h3 className="mb-2 flex items-center gap-2 font-display text-lg font-black uppercase text-red-400">
              <ShieldAlert className="h-4 w-4" /> {t("admin.delete_account_title")}
            </h3>
            <p className="mb-1 font-mono2 text-[11px] text-slate-300">{dialog.user.email}</p>
            <p className="mb-3 text-sm text-slate-400">{t("admin.delete_account_body")}</p>
            <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-wider text-slate-500">{t("admin.delete_account_type")}</label>
            <input
              data-testid="delete-account-confirm-input"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              className="mb-4 w-full rounded border border-[#1F293D] bg-[#060A14] px-2 py-1.5 font-mono2 text-xs text-slate-100 outline-none focus:border-red-500/60"
            />
            <div className="flex justify-end gap-2">
              <button data-testid="delete-account-dismiss" onClick={() => setDialog(null)} className={`${btn} border-slate-600 text-slate-300 hover:bg-slate-700/30`}>{t("admin.cancel")}</button>
              <button
                data-testid="delete-account-confirm"
                disabled={confirmEmail.trim().toLowerCase() !== dialog.user.email}
                onClick={() => act(dialog.user.email, "delete", { confirm_email: confirmEmail.trim().toLowerCase() })}
                className={`${btn} border-red-500 bg-red-500/10 text-red-300 hover:bg-red-500/20 disabled:opacity-40`}
              >
                {t("admin.delete_account_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {dialog?.kind === "access" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="access-level-dialog">
          <div className="w-full max-w-md rounded-xl border border-[#00F0FF]/40 bg-[#0B1120] p-5">
            <h3 className="mb-2 font-display text-lg font-black uppercase text-[#00F0FF]">{t("admin.access_dialog_title")}</h3>
            <p className="mb-3 font-mono2 text-[11px] text-slate-300">{dialog.user.email}</p>
            <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-wider text-slate-500">{t("admin.access_level_label")}</label>
            <select data-testid="access-level-select" value={access.level} onChange={(e) => setAccess({ ...access, level: e.target.value })} className="mb-3 w-full rounded border border-[#1F293D] bg-[#060A14] px-2 py-1.5 font-mono2 text-xs text-slate-100 outline-none">
              <option value="free">{t("admin.access_free")}</option>
              <option value="contributor">{t("admin.access_contributor")}</option>
            </select>
            {access.level === "contributor" && (
              <>
                <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-wider text-slate-500">{t("admin.access_until_label")}</label>
                <input type="date" data-testid="access-until-input" value={access.until} onChange={(e) => setAccess({ ...access, until: e.target.value })} className="mb-3 w-full rounded border border-[#1F293D] bg-[#060A14] px-2 py-1.5 font-mono2 text-xs text-slate-100 outline-none" />
              </>
            )}
            <label className="mb-1 block font-mono2 text-[10px] uppercase tracking-wider text-slate-500">{t("admin.access_note_label")}</label>
            <textarea data-testid="access-note-input" rows={2} value={access.note} placeholder={t("admin.access_note_placeholder")} onChange={(e) => setAccess({ ...access, note: e.target.value })} className="mb-4 w-full rounded border border-[#1F293D] bg-[#060A14] px-2 py-1.5 font-mono2 text-xs text-slate-100 outline-none focus:border-[#00F0FF]/60" />
            <div className="flex justify-end gap-2">
              <button data-testid="access-dismiss" onClick={() => setDialog(null)} className={`${btn} border-slate-600 text-slate-300 hover:bg-slate-700/30`}>{t("admin.cancel")}</button>
              <button data-testid="access-save" disabled={access.note.trim().length < 3} onClick={saveAccess} className={`${btn} border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20 disabled:opacity-40`}>{t("admin.access_save")}</button>
            </div>
          </div>
        </div>
      )}

      <h1 className="mb-4 font-display text-3xl font-black uppercase tracking-tight text-slate-50 sm:text-4xl">{t("admin.users_title")}</h1>
      <AdminNav />

      <div className="mb-8 overflow-x-auto rounded-xl p-4 hud-card" data-testid="users-table">
        <BulkBar
          count={usel.count}
          onClear={usel.clear}
          t={t}
          actions={[
            { key: "suspend", label: t("admin.suspend_account"), cls: "border-amber-500/50 text-amber-400 hover:bg-amber-500/10", onClick: () => bulkUsers("suspend") },
            { key: "reactivate", label: t("admin.reactivate_account"), cls: "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10", onClick: () => bulkUsers("reactivate") },
            { key: "delete", label: t("admin.delete_account"), cls: "border-red-500 bg-red-500/10 text-red-400 hover:bg-red-500/20", onClick: () => bulkUsers("delete") },
          ]}
        />
        <TableToolbar ts={uts} t={t} placeholder={t("admin.email")}>
          <select data-testid="u-filter-plan" className={filterSelectCls} value={uts.get("plan")} onChange={(e) => uts.set("plan", e.target.value)}>
            <option value="">{t("admin.col_subscription")} — {t("filters.all")}</option>
            <option value="free">free</option>
            <option value="premium">premium</option>
          </select>
          <select data-testid="u-filter-access" className={filterSelectCls} value={uts.get("access")} onChange={(e) => uts.set("access", e.target.value)}>
            <option value="">{t("admin.col_access")} — {t("filters.all")}</option>
            <option value="free">{t("admin.access_free")}</option>
            <option value="contributor">{t("admin.access_contributor")}</option>
          </select>
          <select data-testid="u-filter-verified" className={filterSelectCls} value={uts.get("verified")} onChange={(e) => uts.set("verified", e.target.value)}>
            <option value="">{t("admin.col_verified")} — {t("filters.all")}</option>
            <option value="true">{t("admin.yes")}</option>
            <option value="false">{t("admin.no")}</option>
          </select>
          <select data-testid="u-filter-promo" className={filterSelectCls} value={uts.get("promo")} onChange={(e) => uts.set("promo", e.target.value)}>
            <option value="">{t("promo.col_promo")} — {t("filters.all")}</option>
            {[...new Set(users.map((u) => u.promo_code).filter(Boolean))].sort().map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="u-filter-suspended" className={filterSelectCls} value={uts.get("suspended")} onChange={(e) => uts.set("suspended", e.target.value)}>
            <option value="">{t("admin.suspended")} — {t("filters.all")}</option>
            <option value="true">{t("admin.yes")}</option>
            <option value="false">{t("admin.no")}</option>
          </select>
        </TableToolbar>
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-800 font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
              <th className="w-6 py-2 pr-2">
                <SelectAllBox
                  testid="users-select-all"
                  checked={selectable.length > 0 && selectable.every((e) => usel.sel.has(e))}
                  indeterminate={selectable.some((e) => usel.sel.has(e)) && !selectable.every((e) => usel.sel.has(e))}
                  onChange={(on) => usel.setAll(selectable, on)}
                />
              </th>
              <Th k="email" label={t("admin.email")} ts={uts} />
              <Th k="created_at" label={t("admin.col_signed_up")} ts={uts} />
              <Th k="email_verified" label={t("admin.col_verified")} ts={uts} />
              <Th k="plan" label={t("admin.col_subscription")} ts={uts} />
              <Th k="access_level" label={t("admin.col_access")} ts={uts} />
              <Th k="promo_code" label={t("promo.col_promo")} ts={uts} />
              <Th k="last_login" label={t("admin.col_last_login")} ts={uts} />
              <th className="py-2">{t("admin.col_actions")}</th>
            </tr>
          </thead>
          <tbody>
            {uRows.map((u) => (
              <tr key={u.id} className="border-b border-slate-800/60 text-slate-300" data-testid="user-row">
                <td className="py-2 pr-2">
                  {u.role !== "admin" && <RowCheckbox selected={usel.sel.has(u.email)} onToggle={() => usel.toggle(u.email)} testid={`user-select-${u.id}`} />}
                </td>
                <td className="py-2 pr-3">
                  <span className="font-mono2">{u.email}</span>
                  {u.role === "admin" && <span className="ml-2 rounded bg-[#FFD600]/15 px-1.5 py-0.5 font-mono2 text-[9px] font-bold text-[#FFD600]">ADMIN</span>}
                  {u.tax_evidence?.mismatch && <span data-testid={`tax-mismatch-${u.id}`} title={t("admin.tax_mismatch")} className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 font-mono2 text-[9px] font-bold text-amber-400">TVA ≠</span>}
                  {u.suspended && <span className="ml-2 rounded bg-red-500/15 px-1.5 py-0.5 font-mono2 text-[9px] font-bold text-red-400">{t("admin.suspended")}</span>}
                </td>
                <td className="whitespace-nowrap py-2 pr-3 font-mono2 text-[10px] text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="py-2 pr-3">{badge(!!u.email_verified, u.email_verified ? t("admin.yes") : t("admin.no"))}</td>
                <td className="py-2 pr-3">
                  <span className={`font-mono2 text-[10px] font-bold uppercase ${u.plan === "premium" ? "text-[#FFD600]" : "text-slate-500"}`}>{u.plan || "free"}</span>
                </td>
                <td className="py-2 pr-3" data-testid={`u-access-${u.email}`}>
                  <span className={`font-mono2 text-[10px] font-bold uppercase ${u.access_level === "contributor" ? "text-[#00F0FF]" : "text-slate-500"}`}>{u.access_level || "free"}</span>
                  {u.access_level === "contributor" && u.contributor_until && <span className="ml-1 font-mono2 text-[9px] text-slate-500">→ {u.contributor_until.slice(0, 10)}</span>}
                </td>
                <td className="py-2" data-testid={`u-promo-${u.email}`}>
                  {u.promo_code ? (
                    <span className="font-mono2 text-[10px] uppercase text-[#00F0FF]" title={`${u.promo_discount || ""} · ${(u.promo_applied_at || "").slice(0, 10)}`}>
                      {u.promo_code}
                      {u.promo_discount ? <span className="ml-1 text-slate-500">{u.promo_discount}</span> : null}
                      {u.promo_applied_at ? <span className="ml-1 text-slate-600">{(u.promo_applied_at || "").slice(0, 10)}</span> : null}
                    </span>
                  ) : (
                    <span className="font-mono2 text-[10px] text-slate-600">—</span>
                  )}
                  {u.subscription_status && <span className="ml-1.5 font-mono2 text-[9px] text-slate-500">({u.subscription_status})</span>}
                </td>
                <td className="whitespace-nowrap py-2 pr-3 font-mono2 text-[10px] text-slate-500">{u.last_login ? new Date(u.last_login).toLocaleString() : "—"}</td>
                <td className="py-2">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="flex flex-wrap items-center gap-1 rounded-md border border-[#1F293D] bg-[#0B1120]/60 p-1">
                      {u.role !== "admin" && (
                        u.suspended ? (
                          <button data-testid={`reactivate-${u.id}`} onClick={() => act(u.email, "reactivate")} className={`${btn} border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10`}>{t("admin.reactivate_account")}</button>
                        ) : (
                          <button data-testid={`suspend-${u.id}`} onClick={() => act(u.email, "suspend")} className={`${btn} border-amber-500/50 text-amber-400 hover:bg-amber-500/10`}>{t("admin.suspend_account")}</button>
                        )
                      )}
                      {u.role !== "admin" && (
                        <button data-testid={`access-level-${u.id}`} onClick={() => openAccess(u)} className={`${btn} border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10`}>{t("admin.set_access")}</button>
                      )}
                      {(u.plan === "premium" || u.stripe_subscription_id) && u.subscription_status !== "cancel_at_period_end" && (
                        <button data-testid={`cancel-subscription-${u.id}`} onClick={() => openCancel(u)} className={`${btn} border-orange-500/50 text-orange-400 hover:bg-orange-500/10`}>{t("admin.cancel_subscription")}</button>
                      )}
                    </span>
                    {u.role !== "admin" && (
                      <button
                        data-testid={`delete-${u.id}`}
                        onClick={() => { setConfirmEmail(""); setDialog({ kind: "delete", user: u }); }}
                        className={`${btn} ml-2 border-red-500 bg-red-500/10 text-red-400 hover:bg-red-500/20`}
                      >
                        {t("admin.delete_account")}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {uRows.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-slate-600">—</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mb-8 rounded-xl p-4 hud-card" data-testid="newsletter-admin-panel">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-mono2 text-xs font-bold uppercase tracking-widest text-[#00F0FF]">{t("admin.newsletter_panel")}</h2>
          <button onClick={exportCsv} data-testid="newsletter-export-button" className="flex items-center gap-1 rounded-md border border-[#1F293D] px-2.5 py-1.5 font-mono2 text-[10px] uppercase tracking-wider text-slate-300 hover:border-[#00FF87]/50 hover:text-[#00FF87]">
            <Download className="h-3 w-3" /> {t("admin.export_csv")}
          </button>
        </div>
        {nl && (
          <div className="mb-3 flex flex-wrap gap-3 font-mono2 text-[10px] uppercase tracking-wider">
            <span className="text-slate-400">total: <b className="text-slate-100">{nl.total}</b></span>
            <span className="text-emerald-400">confirmed: <b>{nl.confirmed}</b></span>
            <span className="text-amber-400">pending: <b>{nl.pending}</b></span>
            <span className="text-slate-500">unsubscribed: <b>{nl.unsubscribed}</b></span>
          </div>
        )}
        <TableToolbar ts={nts} t={t} placeholder={t("admin.email")}>
          <select data-testid="n-filter-status" className={filterSelectCls} value={nts.get("status")} onChange={(e) => nts.set("status", e.target.value)}>
            <option value="">{t("admin.status")} — {t("filters.all")}</option>
            {nStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </TableToolbar>
        <div className="max-h-48 overflow-y-auto overflow-x-auto">
          {nRows.slice(0, 50).map((s) => (
            <div key={s.email} className="flex items-center justify-between border-b border-slate-800/50 py-1.5 font-mono2 text-[10px]" data-testid="newsletter-subscriber-row">
              <span className="text-slate-300">{s.email}</span>
              <span className={s.status === "confirmed" ? "text-emerald-400" : s.status === "pending" ? "text-amber-400" : "text-slate-600"}>{s.status}</span>
            </div>
          ))}
          {nRows.length === 0 && <p className="py-3 text-center text-xs text-slate-600">—</p>}
        </div>
      </div>

      <div className="rounded-xl p-4 hud-card overflow-x-auto" data-testid="audit-log-panel">
        <h2 className="mb-3 flex items-center gap-2 font-mono2 text-xs font-bold uppercase tracking-widest text-[#D946EF]">
          <ShieldAlert className="h-3.5 w-3.5" /> {t("admin.audit_title")}
        </h2>
        <TableToolbar ts={ats} t={t}>
          <select data-testid="a-filter-action" className={filterSelectCls} value={ats.get("action")} onChange={(e) => ats.set("action", e.target.value)}>
            <option value="">{t("admin.audit_what")} — {t("filters.all")}</option>
            {auditActions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <input type="date" data-testid="a-filter-from" className={filterSelectCls} value={ats.get("from")} onChange={(e) => ats.set("from", e.target.value)} />
          <input type="date" data-testid="a-filter-to" className={filterSelectCls} value={ats.get("to")} onChange={(e) => ats.set("to", e.target.value)} />
        </TableToolbar>
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-800 font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
              <Th k="at" label={t("admin.audit_when")} ts={ats} />
              <Th k="actor" label={t("admin.audit_who")} ts={ats} />
              <Th k="action" label={t("admin.audit_what")} ts={ats} />
              <Th k="target" label={t("admin.audit_target")} ts={ats} />
            </tr>
          </thead>
          <tbody>
            {aRows.map((a, i) => (
              <tr key={i} className="border-b border-slate-800/50 font-mono2 text-[10px] text-slate-400" data-testid="audit-log-row">
                <td className="whitespace-nowrap py-1.5 pr-3 text-slate-500">{a.at ? new Date(a.at).toLocaleString() : "—"}</td>
                <td className="py-1.5 pr-3">{a.actor}</td>
                <td className="py-1.5 pr-3 text-[#00F0FF]">{a.action}</td>
                <td className="py-1.5">{a.target}{a.detail ? ` — ${a.detail}` : ""}</td>
              </tr>
            ))}
            {aRows.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-slate-600">—</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
